import { Avatar, Layout, Menu } from 'antd';
import * as eva from 'eva-icons';
import Gleap from 'gleap';
import { includes, omit } from 'lodash';
import React, { useContext, useEffect } from 'react';
import { Link, useParams } from 'react-router';
import { AppContext } from '../../../AppContext';
import useLazyQueryWithCallbacks from '../../../common/CommonUseLazyQuery';
import useQueryWithCallbacks from '../../../common/CommonUseQuery';
import {
  ACCESS_TYPE,
  ALLOWED_ACTION_KEYS,
  GA_EVENT,
  GA_LABEL,
  MODULES,
  ROUTES,
  SYSTEM_ROLES,
} from '../../../common/constants';
import { Event } from '../../../common/trackEvents';
import useRouter from '../../../common/useRouter';
import CanPerform from '../../../components/CanPerform';
import HasAccess from '../../../components/HasAccess';
import {
  GET_USER_EQC_APPROVAL_LOGS_COUNT,
  GET_USER_REGISTER_APPROVAL_LOGS_COUNT,
  USER_INSTRUCTION_TODO_LIST,
} from '../../../modules/todo/graphql/Queries';
import { GET_PROFILE } from '../../../modules/users/graphql/Queries';
import TenantSelect from './TenantSelect';
import UserProfile from './UserProfile';

const { Header } = Layout;
const initialTodoFilter = {
  skip: 0,
  limit: 10,
  sortBy: { field: 'updatedAt', order: 'DESC' },
  isRfi: false,
};
const initialProjectFilter = {
  skip: 0,
  limit: 10,
  sortBy: { field: 'createdAt', order: 'DESC' },
};
const AppHeader = () => {
  const {
    state: { currentRole, currentUser },
  } = useContext(AppContext);
  const { tenantId } = useParams();
  const hasInspectionAccess = !!HasAccess({ type: ACCESS_TYPE.INSPECTION });
  const hasInstructionAccess = !!HasAccess({ type: ACCESS_TYPE.INSTRUCTION });
  const hasRegisterAccess = !!HasAccess({ type: ACCESS_TYPE.REGISTER });

  const [getUserEqcApprovalLogs, { data }] = useLazyQueryWithCallbacks(
    GET_USER_EQC_APPROVAL_LOGS_COUNT,
    {
      variables: { filter: initialTodoFilter },
      fetchPolicy: 'network-only',
      onError: () => {},
    },
  );
  const [userInstructionTodoList, { data: instructionData }] = useLazyQueryWithCallbacks(
    USER_INSTRUCTION_TODO_LIST,
    {
      variables: { filter: initialProjectFilter },
      onError: () => {},
      fetchPolicy: 'network-only',
    },
  );
  const [getUserRegisterApprovalLogs, { data: registerData }] = useLazyQueryWithCallbacks(
    GET_USER_REGISTER_APPROVAL_LOGS_COUNT,
    {
      variables: { filter: omit(initialTodoFilter, 'isRfi') },
      fetchPolicy: 'network-only',
      onError: () => {},
    },
  );
  const {
    location: { pathname },
  } = useRouter();
  useEffect(() => {
    eva.replace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSelectedMenuKey = () => {
    if (
      pathname.startsWith(ROUTES.STAGE) ||
      pathname.startsWith(ROUTES.REGISTER_APPROVAL)
    ) {
      return ROUTES.TODO;
    }
    if (tenantId) {
      return ROUTES.PROJECTS;
    }
    return `/${pathname.split('/')[1]}`;
  };

  useEffect(() => {
    if (hasInspectionAccess) {
      getUserEqcApprovalLogs();
    }
    if (hasInstructionAccess) {
      userInstructionTodoList();
    }
    if (hasRegisterAccess) {
      getUserRegisterApprovalLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.tenantUser?.tenant?.id]);

  const {
    data: userData,
    loading,
    refetch,
  } = useQueryWithCallbacks(GET_PROFILE, {
    fetchPolicy: 'no-cache',
    onCompleted(res) {
      Gleap.identify(res?.getLoggedInUser?.id, {
        name: res?.getLoggedInUser?.name,
        email: res?.getLoggedInUser?.email,
        phone: res?.getLoggedInUser?.phoneNo,
      });
    },
  });

  useEffect(() => {
    if (currentUser?.tenantUser?.tenant?.id) {
      refetch();
    }
  }, [currentUser?.tenantUser?.tenant?.id]);

  //! for future use
  // const isProjectAdmin = !!filter(
  //   userData?.getLoggedInUser?.projectUsers,
  //   (user) => user?.roles === SYSTEM_ROLES.PROJECT_ADMIN,
  // ).length;
  // const isInspector = !!filter(
  //   userData?.getLoggedInUser?.projectUsers,
  //   (user) => user?.roles === SYSTEM_ROLES.INSPECTOR,
  // ).length;

  // eslint-disable-next-line no-undef
  window.onbeforeunload = closeIt;

  function closeIt() {
    Gleap.clearIdentity();
  }
  const getDefaultTodoRoute = () => {
    if (hasInspectionAccess) return ROUTES.EQC;
    if (hasRegisterAccess) return ROUTES.REGISTER;
    return ROUTES.ISSUES;
  };

  const trackTabClick = (eventName, labelName) => {
    Event(eventName, {
      label: labelName,
      // eslint-disable-next-line no-undef
      pathname: window?.location?.href,
      user_id: currentUser?.id,
      user_name: currentUser?.name,
      tenant_id: currentUser?.tenantUser?.tenant?.id,
      tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
    });
  };

  const items =
    userData?.getLoggedInUser?.isLocal ||
    (!includes(
      [SYSTEM_ROLES.SYSTEM_ADMIN, SYSTEM_ROLES.CHECKLIST_MAKER],
      userData?.getLoggedInUser?.roles?.[0],
    ) &&
      !userData?.getLoggedInUser?.projectUsers?.length > 0)
      ? [
          {
            label: (
              <Link
                to={`${ROUTES.TODO}${getDefaultTodoRoute()}`}
                className="d-flex align-center"
                onClick={() =>
                  trackTabClick(GA_EVENT.TODO_TAB_CLICK, GA_LABEL.TODO_TAB_CLICK)
                }
              >
                {MODULES.TODO}
                {(hasInspectionAccess
                  ? (data?.getUserEqcApprovalLogs?.total ?? 0)
                  : 0) +
                  (hasInstructionAccess
                    ? (instructionData?.userInstructionTodoList?.total ?? 0)
                    : 0) +
                  (hasRegisterAccess
                    ? (registerData?.getUserRegisterApprovalLogs?.total ?? 0)
                    : 0) >
                  0 && (
                  <Avatar className="todo-count" size={25}>
                    {(hasInspectionAccess
                      ? (data?.getUserEqcApprovalLogs?.total ?? 0)
                      : 0) +
                      (hasInstructionAccess
                        ? (instructionData?.userInstructionTodoList?.total ?? 0)
                        : 0) +
                      (hasRegisterAccess
                        ? (registerData?.getUserRegisterApprovalLogs?.total ??
                          0)
                        : 0)}
                  </Avatar>
                )}
              </Link>
            ),
            key: ROUTES.TODO,
          },
        ]
      : [
          ...(CanPerform({
            action: ALLOWED_ACTION_KEYS.VIEW_PROJECTS_PAGE,
          })
            ? [
                {
                  label: (
                    <Link
                      to={ROUTES.PROJECTS}
                      className="d-flex align-center"
                      onClick={() =>
                        trackTabClick(
                          GA_EVENT.PROJECTS_TAB_CLICK,
                          GA_LABEL.PROJECTS_TAB_CLICK,
                        )
                      }
                    >
                      {MODULES.PROJECTS}
                    </Link>
                  ),
                  key: ROUTES.PROJECTS,
                },
              ]
            : []),
          ...(CanPerform({ action: ALLOWED_ACTION_KEYS.VIEW_SETUP_PAGE }) &&
          (currentRole === SYSTEM_ROLES.CHECKLIST_MAKER
            ? hasInspectionAccess
            : true)
            ? [
                {
                  label: (
                    <Link
                      to={`${ROUTES.SETUP}${
                        currentRole === SYSTEM_ROLES.CHECKLIST_MAKER
                          ? ROUTES.CHECKLISTS
                          : ROUTES.USERS
                      }`}
                      className="d-flex align-center"
                      onClick={() =>
                        trackTabClick(
                          GA_EVENT.SETUP_TAB_CLICK,
                          GA_LABEL.SETUP_TAB_CLICK,
                        )
                      }
                    >
                      {MODULES.SETUP}
                    </Link>
                  ),
                  key: ROUTES.SETUP,
                },
              ]
            : []),
          ...((hasInspectionAccess || hasInstructionAccess) &&
          CanPerform({ action: ALLOWED_ACTION_KEYS.VIEW_LOGS_PAGE })
            ? [
                {
                  label: (
                    <Link
                      to={ROUTES.LOGS}
                      className="d-flex align-center"
                      onClick={() =>
                        trackTabClick(
                          GA_EVENT.LOGS_TAB_CLICK,
                          GA_LABEL.LOGS_TAB_CLICK,
                        )
                      }
                    >
                      {MODULES.LOGS}
                    </Link>
                  ),
                  key: ROUTES.LOGS,
                },
              ]
            : []),
          ...(hasInspectionAccess &&
          CanPerform({
            action: ALLOWED_ACTION_KEYS.VIEW_DASHBOARD_PAGE,
          })
            ? [
                {
                  label: (
                    <Link
                      to={ROUTES.MAIN}
                      state={{ fromDashboardTab: true }}
                      className="d-flex align-center"
                      onClick={() =>
                        trackTabClick(
                          GA_EVENT.DASHBOARD_TAB_CLICK,
                          GA_LABEL.DASHBOARD_TAB_CLICK,
                        )
                      }
                    >
                      {MODULES.DASHBOARD}
                    </Link>
                  ),
                  key: ROUTES.MAIN,
                },
              ]
            : []),
          ...((hasInspectionAccess || hasInstructionAccess) &&
          currentRole !== SYSTEM_ROLES.CHECKLIST_MAKER
            ? [
                {
                  label: (
                    <Link
                      to={ROUTES.REPORTS}
                      className="d-flex align-center"
                      onClick={() =>
                        trackTabClick(
                          GA_EVENT.REPORTS_TAB_CLICK,
                          GA_LABEL.REPORTS_TAB_CLICK,
                        )
                      }
                    >
                      {MODULES.REPORTS}
                    </Link>
                  ),
                  key: ROUTES.REPORTS,
                },
              ]
            : []),
          ...(hasInspectionAccess || hasInstructionAccess || hasRegisterAccess
            ? [
                {
                  label: (
                    <Link
                      to={`${ROUTES.TODO}${getDefaultTodoRoute()}`}
                      className="d-flex align-center"
                      onClick={() =>
                        trackTabClick(
                          GA_EVENT.TODO_TAB_CLICK,
                          GA_LABEL.TODO_TAB_CLICK,
                        )
                      }
                    >
                      {MODULES.TODO}
                      {(hasInspectionAccess
                        ? (data?.getUserEqcApprovalLogs?.total ?? 0)
                        : 0) +
                        (hasInstructionAccess
                          ? (instructionData?.userInstructionTodoList?.total ??
                            0)
                          : 0) +
                        (hasRegisterAccess
                          ? (registerData?.getUserRegisterApprovalLogs?.total ??
                            0)
                          : 0) >
                        0 && (
                        <Avatar className="todo-count" size={25}>
                          {(hasInspectionAccess
                            ? (data?.getUserEqcApprovalLogs?.total ?? 0)
                            : 0) +
                            (hasInstructionAccess
                              ? (instructionData?.userInstructionTodoList
                                  ?.total ?? 0)
                              : 0) +
                            (hasRegisterAccess
                              ? (registerData?.getUserRegisterApprovalLogs
                                  ?.total ?? 0)
                              : 0)}
                        </Avatar>
                      )}
                    </Link>
                  ),
                  key: ROUTES.TODO,
                },
              ]
            : []),
        ];
  return (
    <Header>
      <>
        <div>
          <TenantSelect userData={userData} />
        </div>
        <div className="gx-search-bar gx-d-none gx-d-lg-block gx-lt-icon-search-bar-lg">
          <div className="header d-flex align-center justify-center">
            <Menu
              defaultSelectedKeys={[
                hasInspectionAccess ? ROUTES.MAIN : ROUTES.PROJECTS,
              ]}
              items={loading ? [] : items}
              theme="light"
              mode="horizontal"
              selectedKeys={[getSelectedMenuKey()]}
            />
          </div>
        </div>
        <div className="header-notification">
          <UserProfile userData={userData} />
        </div>
      </>
    </Header>
  );
};
export default AppHeader;
